import { useEffect, useMemo, useRef, useState } from 'react';
import { Filter, FilterConfig } from 'types/Search';

export type SearchResultsCountFunction = (changes: { [key: string]: FilterConfig }) => Promise<number>;

export type Args = {
  // This is a function that can take the current state of draft/in-progress filters and return a new result count based on those filters.
  searchResultsCountFunction: SearchResultsCountFunction;
  resultsCount: number;
};

const useHypotheticalSearchResultsCount = ({ searchResultsCountFunction, resultsCount }: Args) => {
  const isMounted = useRef(false);

  const [projectedResultsCount, setProjectedResultsCount] = useState(null);

  const computedResultsCount: number = useMemo(() => {
    // null implies that we haven't tried to determine any hypothetical results yet.
    if (projectedResultsCount === null) {
      return resultsCount;
    }

    if (projectedResultsCount !== resultsCount) {
      return projectedResultsCount;
    }

    return resultsCount;
  }, [projectedResultsCount, resultsCount]);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  return {
    resetHypotheticalResultsCount: () => {
      setProjectedResultsCount(null);
    },
    computedResultsCount,
    performHypotheticalSearch: (filtersByKey: { [key: string]: Filter }) => {
      const allDraftConfigsByKey = Object.keys(filtersByKey).reduce((combined, nextKey) => {
        return {
          ...combined,
          [nextKey]: filtersByKey[nextKey].config
        }
      }, {});

      searchResultsCountFunction(allDraftConfigsByKey).then(results => {
        if (!isMounted.current) {
          return;
        }

        setProjectedResultsCount(results);
      });
    }
  };
};

export default useHypotheticalSearchResultsCount;
